import { useAuth } from '@/components/auth/AuthProvider';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3013';

interface ApiError extends Error {
  status?: number;
  response?: any;
}

class ApiService {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    token?: string,
    retryCount = 0,
    maxRetries = 3
  ): Promise<T> {
    const url = `${this.baseURL}/api${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Add Authorization header if token is provided
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestConfig = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, requestConfig);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error || `HTTP error! status: ${response.status}`) as ApiError;
        error.status = response.status;
        error.response = errorData;
        
        // Don't retry on client errors (4xx) - these are usually auth/validation issues
        if (response.status >= 400 && response.status < 500) {
          if (response.status === 401 || response.status === 403) {
            console.error(`Authentication/Authorization error (${response.status}):`, errorData.error || 'Access denied');
          }
          throw error;
        }
        
        // Retry on server errors (5xx) and rate limiting (429)
        if ((response.status >= 500 || response.status === 429) && retryCount < maxRetries) {
          const delay = 1000 * Math.pow(2, retryCount); // Exponential backoff
          console.warn(`Retrying ${endpoint} after error (${retryCount + 1}/${maxRetries}) in ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.makeRequest<T>(endpoint, options, token, retryCount + 1, maxRetries);
        }
        
        throw error;
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      // Retry on network errors
      if (error instanceof TypeError && error.message.includes('fetch') && retryCount < maxRetries) {
        const delay = 1000 * Math.pow(2, retryCount); // Exponential backoff
        console.warn(`Network error, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries}):`, error.message);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.makeRequest<T>(endpoint, options, token, retryCount + 1, maxRetries);
      }
      
      if (error instanceof Error) {
        console.error(`API request failed for ${endpoint}:`, error.message);
      }
      throw error;
    }
  }

  async get<T>(endpoint: string, token?: string): Promise<T> {
    return this.makeRequest<T>(endpoint, { method: 'GET' }, token);
  }

  async post<T>(endpoint: string, data?: any, token?: string): Promise<T> {
    return this.makeRequest<T>(
      endpoint,
      {
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      },
      token
    );
  }

  async put<T>(endpoint: string, data?: any, token?: string): Promise<T> {
    return this.makeRequest<T>(
      endpoint,
      {
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
      },
      token
    );
  }

  async delete<T>(endpoint: string, token?: string): Promise<T> {
    return this.makeRequest<T>(endpoint, { method: 'DELETE' }, token);
  }  // New method to handle SSE (Server-Sent Events) responses
  async postSSE<T>(
    endpoint: string,
    data?: any,
    token?: string
  ): Promise<T> {
    const url = `${this.baseURL}/api${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if token is provided
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: data ? JSON.stringify(data) : undefined,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error || `HTTP error! status: ${response.status}`) as ApiError;
        error.status = response.status;
        error.response = errorData;
        throw error;
      }

      // Handle SSE response
      const responseText = await response.text();

      // Check if response is empty
      if (!responseText.trim()) {
        throw new Error('Empty response received from server');
      }

      // First, check if the entire response is a plain error message (not SSE format)
      if (!responseText.includes('data: ') && responseText.toLowerCase().includes('error')) {
        throw new Error(`Server error: ${responseText.trim()}`);
      }

      // Parse SSE format - collect all text events and concatenate them
      const lines = responseText.split('\n');
      let fullText = '';
      let hasError = false;
      let errorMessage = '';
      let hasValidData = false;
      let chatId = '';
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const eventData = line.substring(6).trim(); // Remove 'data: ' prefix and trim
          
          // Skip empty data lines
          if (!eventData) continue;
          
          try {
            const event = JSON.parse(eventData);
            hasValidData = true;
            
            // Check for error
            if (event.error) {
              hasError = true;
              errorMessage = event.error;
              break;
            }
            
            // Extract chat ID from first event
            if (event.chatId && !chatId) {
              chatId = event.chatId;
            }
            
            // Collect text
            if (event.text) {
              fullText += event.text;
            }
            
            // If this is the last event, we're done
            if (event.is_last) {
              break;
            }
          } catch (parseError) {
            // If we can't parse as JSON, it might be a plain text error
            console.warn('Failed to parse SSE event:', eventData);
            
            // Check if it looks like an error message
            if (eventData.toLowerCase().includes('error') || 
                eventData.toLowerCase().includes('overloaded') ||
                eventData.toLowerCase().includes('503')) {
              hasError = true;
              errorMessage = eventData;
              break;
            }
          }
        }
      }
      
      if (hasError) {
        throw new Error(errorMessage || 'Unknown server error');
      }
      
      if (!hasValidData) {
        throw new Error('No valid SSE data found in response');
      }
      
      if (!fullText) {
        throw new Error('No text data found in SSE response');
      }

      // Return the concatenated response in the expected format
      return {
        success: true,
        data: {
          response: fullText,
          chatId: chatId // Include chat ID in response
        }
      } as T;
    } catch (error) {
      if (error instanceof Error) {
        console.error(`SSE request failed for ${endpoint}:`, error.message);
      }
      throw error;
    }
  }
}

// Create a singleton instance
export const apiService = new ApiService();

// Hook to use API service with authentication
export const useApiService = () => {
  const { session } = useAuth();
  
  const makeAuthenticatedRequest = async <T>(
    method: 'get' | 'post' | 'put' | 'delete' | 'postSSE',
    endpoint: string,
    data?: any,
    retryCount = 0
  ): Promise<T> => {
    try {
      // Import auth service dynamically to avoid circular dependencies
      const { authService } = await import('../services/auth-service');
      
      // Get a valid access token (will refresh if needed)
      const token = await authService.getValidAccessToken();
      
      if (!token) {
        throw new Error('No valid authentication token available');
      }

      switch (method) {
        case 'get':
          return apiService.get<T>(endpoint, token);
        case 'post':
          return apiService.post<T>(endpoint, data, token);
        case 'postSSE':
          return apiService.postSSE<T>(endpoint, data, token);
        case 'put':
          return apiService.put<T>(endpoint, data, token);
        case 'delete':
          return apiService.delete<T>(endpoint, token);
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }
    } catch (error: any) {
      // Handle authentication/authorization errors
      if (error.status === 401 && retryCount === 0) {
        console.log('Received 401 error, attempting token refresh...');
        
        // Import auth service dynamically to avoid circular dependencies
        const { authService } = await import('../services/auth-service');
        const refreshSuccess = await authService.handleUnauthorizedError();
        
        if (refreshSuccess) {
          console.log('Token refresh successful, retrying request...');
          // Retry the request once with the new token
          return makeAuthenticatedRequest<T>(method, endpoint, data, retryCount + 1);
        }
      } else if (error.status === 401 || error.status === 403) {
        console.error('Authentication/authorization error:', error.message);
        // Import auth service dynamically to avoid circular dependencies
        import('../services/auth-service').then(({ authService }) => {
          authService.handleAuthFailure();
        });
      }
      throw error;
    }
  };

  return {
    get: <T>(endpoint: string) => makeAuthenticatedRequest<T>('get', endpoint),
    post: <T>(endpoint: string, data?: any) => makeAuthenticatedRequest<T>('post', endpoint, data),
    postSSE: <T>(endpoint: string, data?: any) => makeAuthenticatedRequest<T>('postSSE', endpoint, data),
    put: <T>(endpoint: string, data?: any) => makeAuthenticatedRequest<T>('put', endpoint, data),
    delete: <T>(endpoint: string) => makeAuthenticatedRequest<T>('delete', endpoint),
  };
};

export default apiService;
